/**
 * P3-28: AutoPinnedButton —— leviathans 专用 auto-pin 按钮。
 *
 * 迁移自 `classes.diplomacy.ui.autoPinnedButton` +
 * `autoPinnedButtonController`（js/diplomacy.js:1448-1498）。
 *
 * 行为对齐：
 * - 主按钮点击：切换 `race.autoPinned`
 * - pin 链接：切换 `race.pinned`
 */
import { useCallback, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton, GameButtonLink } from "../../components/GameButton";
import { useAutoPinnedButtonModel, type RaceLike } from "./useTradeModel";

interface AutoPinnedButtonProps {
  raceName: string;
}

export function AutoPinnedButton({ raceName }: AutoPinnedButtonProps) {
  const { snap, modelRef, controller, getTooltip } = useAutoPinnedButtonModel(raceName);

  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const togglePin = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const game = getGame() as (GamePage & { diplomacy?: { get: (n: string) => RaceLike } }) | null;
      const race = game?.diplomacy?.get(raceName);
      if (race) {
        race.pinned = !race.pinned;
      }
    },
    [raceName],
  );

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      getTooltip={getTooltip}
      onClick={onClick}
    >
      <GameButtonLink
        link={{
          title: snap.pinned ? "[v]" : "[ ]",
          tooltip: t("trade.embassy.pinned"),
          handler: togglePin,
        }}
      />
    </GameButton>
  );
}
